import { REGEX_PATTERNS, MINIMUM_AGE, YEARS } from "~/constants/contact-credit";
import { formatPhone, unformatPhone } from "~/helpers/phone";
import { UseFormGetValues, UseFormSetError, Path, UseFormClearErrors } from "react-hook-form";
import { Credit, UseFormClearCreditErrors } from "~/types/contact-credit";

const suffixes = ["JR", "SR", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX"];

const getSuffix = (value: string): { words: string[]; suffix: string } => {
  const trimmedvalue = value?.trim();
  const words = trimmedvalue.split(" ");

  return {
    words,
    suffix: words[words.length - 1]?.toUpperCase(),
  };
};

export const formatSmartQuotes = (value: string): string => {
  return value.replace(/[\u2018\u2019]/g, "'");
};

export const formatNameSpacing = (value = "", trimAllSpaces = false): string => {
  const str = value
    // Remove leading and trailing spaces
    .trim()
    // Replace consecutive spaces with one space
    .replace(/\s+/g, " ");

  // Optional: Remove leading and trailing spaces around periods, apostrophes, and hyphens
  return trimAllSpaces ? str.replace(/\s*([.,'-])\s*/g, "$1") : str;
};

export const validateFirstName = (value: string): string | boolean => {
  if (!value) return "First Name is required.";
  if (!REGEX_PATTERNS.NAME.test(value)) {
    return "First Name may only contain letters, periods, apostrophes, hyphens or spaces";
  }
  const { suffix } = getSuffix(value);
  if (suffixes.includes(suffix)) return "Move suffix to Last Name";
  return true;
};

export const validateLastName = (value: string): string | boolean => {
  if (!value) return "Last Name is required.";
  if (!REGEX_PATTERNS.NAME.test(value))
    return "Last Name may only contain letters, periods, apostrophes, hyphens or spaces";

  return true;
};

// Date validation helper
export const validateDateOfBirth = <T extends Credit>(
  getValues: UseFormGetValues<T>,
  setError: UseFormSetError<T>,
  clearErrors: UseFormClearErrors<T>,
  checkComplete = false
): boolean => {
  const dob = getValues("dob" as Path<T>) as string;

  if (!dob) {
    setError("dob" as Path<T>, {
      type: "required",
      message: "Date of birth is required",
    });
    return false;
  }

  // If checking for completion (onBlur) and the date is partial
  if (checkComplete && !/^\d{2}\/\d{2}\/\d{4}$/.test(dob as string)) {
    setError("dob" as Path<T>, {
      type: "incomplete",
      message: "Please enter a complete date (MM/DD/YYYY)",
    });
    return false;
  }

  // Only proceed with full validation if we have a complete date
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dob as string)) {
    if (checkComplete) {
      setError("dob" as Path<T>, {
        type: "format",
        message: "Please enter a complete date (MM/DD/YYYY)",
      });
      return false;
    }
    return true; // Allow partial dates during typing
  }

  const [month, day, year] = dob.split("/").map(Number);

  // Basic date validation
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) {
    setError("dob" as Path<T>, {
      type: "invalid",
      message: "Invalid date",
    });
    return false;
  }

  // Check if date is valid (handles edge cases like 02/31/2000)
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1) {
    setError("dob" as Path<T>, {
      type: "invalid",
      message: "Invalid date",
    });
    return false;
  }

  // Get current date without time
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate birthdate
  const birthDate = new Date(year, month - 1, day);
  birthDate.setHours(0, 0, 0, 0);

  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // Adjust age if birthday hasn't occurred this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  const aboveMinimumAge = age >= MINIMUM_AGE;

  if (aboveMinimumAge) {
    clearErrors(["dob"] as Path<T>[]);
    return true;
  } else {
    setError("dob" as Path<T>, {
      type: "age",
      message: `Must be ${MINIMUM_AGE} or older`,
    });
    return false;
  }
};

export const formatDateInput = (value: string): string => {
  // Remove non-digits
  const digits = value.replace(/\D/g, "");

  // Format with slashes
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
};

export const validateMinimumAge = <T extends Credit>(
  getValues: UseFormGetValues<T>,
  setError: UseFormSetError<T>,
  clearErrors: UseFormClearCreditErrors<T>
): boolean => {
  const { month, day, year } = getValues();
  if (![month, day, year].every(Boolean)) return true;

  if (!YEARS.map((yearOption) => yearOption.value.toString()).includes(String(year))) {
    setError("year" as Path<T>, { type: "custom" });
    return false;
  }

  const today = new Date().getTime();
  const dob = new Date(`${year}-${month}-${day}`);
  const dobPlusMinAge = new Date(dob.getFullYear() + MINIMUM_AGE, dob.getMonth(), dob.getDate());
  dobPlusMinAge.setUTCHours(0, 0, 0, 0);
  const aboveMinimumAge = today >= dobPlusMinAge.getTime();
  if (aboveMinimumAge) {
    clearErrors(["month", "day", "year"]);
  } else {
    setError("month" as Path<T>, { type: "custom" });
    setError("day" as Path<T>, { type: "custom" });
    setError("year" as Path<T>, { type: "custom" });
  }
  return aboveMinimumAge;
};

/* North American Numbering Plan has a few basic rules:
   - Area codes start with a number 2–9, followed by 0–8 (9 reserved for future use), and then any third digit
   - The second group of three digits, known as the central office or exchange code, starts with a number 2–9, followed by any two digits
   - The final four digits, known as the station code, have no restrictions
   See: https://en.wikipedia.org/wiki/North_American_Numbering_Plan#Modern_plan

   This satisfies the Pareto principle where these basic rules will catch a large majority of invalid numbers

   There are additional Numbering Plan rules that exist but may not be worth implementing:
    - N11 Code (211, 311, 411, 511, 611, 711, 811, and 911) reserved. Phone not valid: (234) 911-5678 or (xxx) N11-xxxx
    - Directory assistance: (area code)-555-1212
    - Only 555-0100 through 555-0199 are now specifically reserved for fictional use (e.g. movies, tv shows, games);
    - Toll Free area code prefix: 800, 833, 844, 855, 866, 877, and 888; Also, 822, 880 through 887, and 889 reserved for future use
*/
export const validatePhoneNumber = (value: string): string | boolean => {
  const phone = unformatPhone(value);
  const [area, exchange] = formatPhone(phone).split("-");

  if (!phone.length) return "Phone number is required.";
  if (phone.length < 10) return "Invalid phone number. 10-digits required (area code + phone).";

  // uses a backreference to match a single digit that is repeated 9 times. Ex: 2222222222
  if (REGEX_PATTERNS.PHONE.REPEATING.test(phone)) return "Invalid phone number. No repeated digits.";

  if (!REGEX_PATTERNS.PHONE.AREA.test(area)) return "Area code is invalid.";
  if (!REGEX_PATTERNS.PHONE.EXCHANGE.test(exchange)) return "Invalid phone number. Avoid 0 or 1 after area code.";

  return true;
};

export const convertSuffixToUpperCase = (name: string): string => {
  if (typeof name !== "string" || !name?.length) return "";

  const { words, suffix } = getSuffix(name);

  // Check if the last word is a valid suffix and update it
  if (suffixes.includes(suffix)) {
    words[words.length - 1] = suffix;
  }

  // Rejoin the words and return the updated string
  return words.join(" ");
};

const formatDateParts = (value: string): { month: string; day: string; year: string } => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return {
    month: digits.slice(0, 2),
    day: digits.slice(2, 4),
    year: digits.slice(4, 8),
  };
};

const addSlashes = (month: string, day: string, year: string): string => {
  let formatted = month;
  if (month.length === 2) formatted += "/";
  formatted += day;
  if (day.length === 2) formatted += "/";
  formatted += year;
  return formatted;
};

const handleSlashDeletion = (formatted: string, previousValue: string, cursorPos: number): string => {
  if (previousValue.length > formatted.length && previousValue[cursorPos - 1] === "/" && cursorPos > 0) {
    return formatted.slice(0, cursorPos - 1) + formatted.slice(cursorPos);
  }
  return formatted;
};

const adjustCursorPosition = (formatted: string, previousValue: string, cursorPos: number): number => {
  const slashCountBefore = (previousValue.slice(0, cursorPos).match(/\//g) || []).length;
  const slashCountAfter = (formatted.slice(0, cursorPos).match(/\//g) || []).length;
  return cursorPos + (slashCountAfter - slashCountBefore);
};

/**
 * Formats date input as MM/DD/YYYY, handling slash insertion/deletion and cursor position.
 * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
 * @example
 * // Input: "12312000" -> Output: "12/31/2000"
 * // Input: "12" -> Output: "12/"
 */
export const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
  const { month, day, year } = formatDateParts(e.target.value);
  const formatted = addSlashes(month, day, year);
  const cursorPos = e.target.selectionStart || 0;
  const previousValue = e.target.value;

  const finalFormatted = handleSlashDeletion(formatted, previousValue, cursorPos);
  e.target.value = finalFormatted;

  const correctedCursorPos = adjustCursorPosition(finalFormatted, previousValue, cursorPos);
  e.target.setSelectionRange(correctedCursorPos, correctedCursorPos);
};
